import React from 'react';
import { useQuery } from 'react-query';
import api from '../../api/client';
import endpoints from '../../api/endpoints';
import LoadingSpinner from '../LoadingSpinner';
import { Users, MessageSquare, FileText, AlertCircle } from 'lucide-react';

const OverviewTab = () => {
  const { data: userStats, isLoading: loadingUsers } = useQuery(
    'admin-users',
    () => api.get(endpoints.auth.users()).then(res => res.data)
  );

  const { data: docStats, isLoading: loadingDocs } = useQuery(
    'admin-doc-stats',
    () => api.get(endpoints.documents.stats).then(res => res.data)
  );

  const { data: convStats, isLoading: loadingConv } = useQuery(
    'admin-conv-stats',
    () => api.get(endpoints.conversations.stats).then(res => res.data)
  );

  const { data: feedbackData, isLoading: loadingFeedback } = useQuery(
    'admin-feedback-stats',
    () => api.get(`${endpoints.feedback.list}?limit=1000`).then(res => res.data)
  );

  if (loadingUsers || loadingDocs || loadingConv || loadingFeedback) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  const stats = [
    {
      label: 'Total Users',
      value: userStats?.users?.length || 0,
      icon: Users,
      color: 'primary'
    },
    {
      label: 'Conversations',
      value: convStats?.stats?.totalThreads || 0,
      icon: MessageSquare,
      color: 'secondary'
    },
    {
      label: 'Documents',
      value: docStats?.totalDocuments || 0,
      icon: FileText,
      color: 'success'
    },
    {
      label: 'Pending Feedback',
      value: feedbackData?.feedback?.filter(f => f.status === 'pending').length || 0,
      icon: AlertCircle,
      color: 'warning'
    }
  ];

  return (
    <div className="overview-tab">
      <h2 className="tab-section-title">System Overview</h2>
      
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card ${stat.color}`}>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-value">{stat.value}</div>
              <Icon size={32} style={{ opacity: 0.3, marginTop: '8px' }} />
            </div>
          );
        })}
      </div>

      <div className="tab-section">
        <h3 className="tab-section-title">Recent Activity</h3>
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="stat-label">Total Messages</div>
            <div className="stat-value">{convStats?.stats?.totalMessages || 0}</div>
            <div className="stat-change">Across all conversations</div>
          </div>
          
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <div className="stat-label">Avg Messages/Thread</div>
            <div className="stat-value">{Math.round(convStats?.stats?.avgMessagesPerThread || 0)}</div>
            <div className="stat-change">Per conversation</div>
          </div>
          
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <div className="stat-label">Total Tokens Used</div>
            <div className="stat-value">{(convStats?.stats?.totalTokens || 0).toLocaleString()}</div>
            <div className="stat-change">API usage</div>
          </div>
        </div>
      </div>

      <div className="tab-section">
        <h3 className="tab-section-title">User Distribution</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {['admin', 'researcher', 'viewer'].map(role => {
              const count = userStats?.users?.filter(u => u.role === role).length || 0;
              const total = userStats?.users?.length || 1;
              const percentage = ((count / total) * 100).toFixed(1);
              return (
                <tr key={role}>
                  <td style={{ textTransform: 'capitalize' }}>{role}</td>
                  <td>{count}</td>
                  <td>{percentage}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OverviewTab;
